import React, { useState, useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { debounce } from 'lodash'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { selectBuildingsForFilter } from '../../../../reducers/buildingSelectorReducer'
import {
  fetchLoyaltyBuildingRewards,
  selectLoyaltyRewards,
  updateBuildingRewardStatus,
} from '../../../../reducers/loyaltyReducer'
import SearchField from '../../../FormLegacy/FormFieldsV2/SearchField'
import PaginationFooter from '../../../PaginationFooter/PaginationFooter'
import FeaturedTable from '../../../FeaturedTable/FeaturedTable'
import RewardsRewardDetailsModal from '../RewardsRewardDetailsModal/RewardsRewardDetailsModal'
import { getSettingsTabs, rewardsSettingsColumns } from '../helpers'
import { useSnackbar } from '../../../../helpers/hooks/useSnackbar'

const RewardsSettings = ({
  fetchLoyaltyBuildingRewards = () => {},
  updateBuildingRewardStatus = () => {},
  buildings,
  rewards = [],
}) => {
  const { t } = useTranslation(['rewards-table'])
  const settingsTabs = getSettingsTabs(t)
  const [selectedTab, setSelectedTab] = useState('')
  const [search, setSearch] = useState('')
  const [isLoading, setLoading] = useState(false)
  const [showOnPage, setShowOnPage] = useState(25)
  const [pageNumber, setPageNumber] = useState(1)
  const [paginationItemsCount, setPaginationItemsCount] = useState(0)
  const [prevSearchValue, setPrevSearchValue] = useState('')
  const [detailReward, setDetailReward] = useState(null)

  const { displaySnackbar } = useSnackbar()
  const { buildingId } = useParams()

  const fetchRewards = async (query) => {
    setLoading(true)

    const requestData = {
      buildingId: buildingId,
      page: query ? 1 : pageNumber,
      limit: showOnPage,
      type: selectedTab && selectedTab.value ? selectedTab.value : undefined,
      name: query || search,
    }

    await fetchLoyaltyBuildingRewards(requestData)
      .then((count) => setPaginationItemsCount(count))
      .catch(({ message }) => displaySnackbar(message, true))
    setLoading(false)
  }

  const selectedBuilding = useMemo(
    () => buildings.find((building) => building.id === Number(buildingId)),
    [buildings, buildingId],
  )

  useEffect(() => {
    if (selectedBuilding) {
      fetchRewards()
    }
  }, [pageNumber, showOnPage, selectedTab, selectedBuilding])

  const debouncedFetch = debounce(fetchRewards, 800)

  useEffect(() => {
    if (prevSearchValue !== search) {
      window.addEventListener('keydown', debouncedFetch(search))
    }
    setPrevSearchValue(search)
    return () => {
      window.removeEventListener('keydown', debouncedFetch)
    }
  }, [search])

  const onTabChange = (tabFilter) => {
    setPageNumber(1)
    setSelectedTab(tabFilter)
  }

  const onSearchChange = (e) => {
    setPageNumber(1)
    setSearch(e.target.value)
  }

  const onSwitchStatus = async (rewardId, status) => {
    try {
      await updateBuildingRewardStatus(rewardId, status)
    } catch ({ message }) {
      displaySnackbar(message, true)
    }
  }

  return (
    <React.Fragment>
      <div className="rewards-container">
        <div className="rewards-table">
          <div className="rewards-table__wrap">
            <div className="rewards-table__filters">
              <div className="rewards-table__filters__group">
                {settingsTabs.map((tabFilter) => (
                  <button
                    className={`rewards-filter-item ${
                      selectedTab && tabFilter.id === selectedTab.id ? 'active' : ''
                    }`}
                    key={tabFilter.id}
                    onClick={() => onTabChange(tabFilter)}
                    disabled={isLoading}
                  >
                    <p style={{ display: 'inline-block' }}>{tabFilter.text} </p>
                  </button>
                ))}
              </div>
              <div className="rewards-table__filters__group">
                <div className={'rewards-table__filters__search'}>
                  <SearchField
                    placeholder={t('rewards-table:searchByRewardNamePlaceholder')}
                    value={search}
                    onChange={onSearchChange}
                  />
                </div>
              </div>
            </div>
            <div className="rewards-table" style={{ padding: 0 }}>
              <div className={`ctp-rewards-loyalty-list`}>
                <FeaturedTable
                  columns={rewardsSettingsColumns({
                    onSwitch: onSwitchStatus,
                    onDetails: setDetailReward,
                    t,
                  })}
                  data={rewards}
                />
              </div>
            </div>
            {rewards.length > 0 && (
              <PaginationFooter
                showOnPage={showOnPage}
                pageNumber={pageNumber}
                paginationItemsCount={paginationItemsCount}
                setSiteLoading={setLoading}
                setShowOnPage={setShowOnPage}
                setPageNumber={setPageNumber}
              />
            )}
          </div>
        </div>
      </div>
      {detailReward && (
        <RewardsRewardDetailsModal onClose={() => setDetailReward(null)} reward={detailReward} />
      )}
    </React.Fragment>
  )
}

RewardsSettings.propTypes = {
  fetchLoyaltyBuildingRewards: PropTypes.func,
  updateBuildingRewardStatus: PropTypes.func,
  buildings: PropTypes.array,
  rewards: PropTypes.array,
}

export default connect(
  (state) => ({
    buildings: selectBuildingsForFilter(state),
    rewards: selectLoyaltyRewards(state),
  }),
  {
    fetchLoyaltyBuildingRewards,
    updateBuildingRewardStatus,
  },
)(RewardsSettings)
